/** @jsxImportSource @emotion/react */
import { Flyout, FlyoutProps } from "components/Flyout/Flyout";
import { useDatasetDataContext } from "pages/project/dataset/DatasetDataProvider";
import { EuiFlexItem, EuiText } from "@inscopix/ideas-eui";
import {
  FileBrowser,
  FileBrowserFile,
  FileBrowserProps,
} from "../FileBrowser/FileBrowser";
import { DraggableFileBadge } from "components/RecordingsGrid/DraggableFileBadge";
import { useDatasetLayoutContext } from "pages/project/dataset/DatasetLayoutProvider";
import { memo, useMemo } from "react";
import { useDatasetAction } from "hooks/useDatasetAction/useDatasetAction";
import { DropZoneBasic } from "components/DropZoneBasic/DropZoneBasic";
import { useDragState } from "hooks/useDragState";
import { css } from "@emotion/react";
import { useProjectFilesStore } from "stores/project-files/ProjectFilesManager";
import { uuid } from "utils/uuid";
import {
  Dataset,
  DatasetRecordingsTableColumn,
  FileRecordingGroup,
} from "graphql/_Types";
import { isDefined } from "utils/isDefined";
import { useGetFileBrowserActions } from "./useGetFileBrowserActions";
import { SetNonNullable } from "type-fest";
import { RecordingIdentifierBadge } from "components/RecordingIdentifierBadge/RecordingIdentifierBadge";
import { FileBrowserDetailRendererAnalysisResult } from "components/FileBrowser/FileBrowserDetailRendererAnalysisResult";
import { FileSource } from "types/constants";

const fileBrowserZIndex = 100;
const styles = {
  mainFlexContainer: css`
    height: 100%;
    min-height: 0px;
    flex: 4;
    position: relative;
  `,
  dropZone: css`
    display: flex;
    position: absolute;
    bottom: 0;
    z-index: ${fileBrowserZIndex * 2};
    border: 1px dashed lightgray;
    height: 100%;
    width: 100%;
    background-color: white;
    border-radius: 5px;
    align-items: center;
    justify-content: center;
  `,
  fileBrowser: css`
    z-index: ${fileBrowserZIndex};
  `,
};

export type FlyoutFilesFile = FileBrowserFile & {
  datasets: Pick<Dataset, "id" | "name" | "name">[];
  assignments:
    | {
        dataset: Pick<Dataset, "id" | "prefix">;
        recording: SetNonNullable<
          Pick<FileRecordingGroup, "id" | "number">,
          "number"
        >;
      }[]
    | [
        {
          dataset: Pick<Dataset, "id" | "prefix">;
          recording: SetNonNullable<
            Pick<FileRecordingGroup, "id" | "number">,
            "number"
          >;
          columnId: DatasetRecordingsTableColumn["id"];
        },
      ];
};
export const flyoutFilesExtraColumns: NonNullable<
  FileBrowserProps<FlyoutFilesFile, FlyoutFilesExtraColId>["extraColumns"]
> = [
  {
    id: "dataset",
    schema: "text",
    isSearchable: true,
    displayAsText: "Dataset",
  },
  {
    id: "assignment",
    schema: "text",
    isSearchable: true,
    displayAsText: "Recording",
  },
  {
    id: "source",
    displayAsText: "Source",
    isSearchable: false,
    schema: "source",
  },
];

export const flyoutFilesDefaultColumnOrder = (() => {
  const order: FileBrowserProps<
    FlyoutFilesFile,
    FlyoutFilesExtraColId
  >["defaultColumnOrder"] = [
    "name",
    "dateCreated",
    "source",
    "status",
    "user",
    "tags",
    "dataset",
    "assignment",
    "size",
    "isSeries",
  ];
  return order;
})();

export interface FlyoutFilesProps extends Pick<FlyoutProps, "headerControls"> {
  onClose: () => void;
}

type FlyoutFilesExtraColId = "dataset" | "assignment" | "source";

export const flyoutFilesExtraColumnProps: NonNullable<
  FileBrowserProps<FlyoutFilesFile, FlyoutFilesExtraColId>["extraColumnProps"]
> = {
  assignment: {
    // /**
    //  * In isolation sorting assignments would be done in steps (prefix then number).
    //  * However given the current API, string concatenation is the most straightforward way
    //  * to achieve the same result without reworking sorting entirely and potentially breaking something
    //  */
    sortValueGetter: (file) =>
      file.assignments
        .map(({ dataset, recording }) => {
          // pad number with leading zeroes to ensure proper sorting
          const paddedNumber = `${recording.number}`.padStart(12, "0");
          // use 0 as delimiter so shorter matching prefixes sort before longer matching prefixes (A before AA)
          return `${dataset.prefix}0${paddedNumber}`;
        })
        .sort()
        .join(", ") ??
      // sort assigned first
      "~~~~~~",
    cellRenderer: ({ file }) => (
      <div style={{ display: "flex" }}>
        {file.assignments.map(({ recording }) => (
          <RecordingIdentifierBadge
            key={recording.id}
            recordingId={recording.id}
            showShortIdOnly
          />
        ))}
      </div>
    ),
  },
  dataset: {
    cellRenderer: ({ file }) => {
      return file.datasets
        .map(({ name }) => name)
        .sort()
        .join(",");
    },
    sortValueGetter: (file) =>
      file.datasets.length > 0
        ? file.datasets
            .map(({ name }) => name)
            .sort()
            .join(", ")
        : // sort files with dataset first
          "~~~~~~",
  },
  source: {
    cellRenderer: ({ file }) => {
      const { source } = file;
      if (source === FileSource.UPLOADED) {
        return "Uploaded";
      }
      if (source === FileSource.ANALYSIS_RESULT) {
        return "Analysis Result";
      }
      throw new Error("Unhandled source type in FileBrowser");
    },
    sortValueGetter: ({ source }) => source,
    isDetails: (file) => file.source === FileSource.ANALYSIS_RESULT,
    detailRenderer: (file) => (
      <div>
        <FileBrowserDetailRendererAnalysisResult drsFile={file} />
      </div>
    ),
  },
};

export const FlyoutFiles = memo(function FlyoutFiles({
  onClose,
  ...props
}: FlyoutFilesProps) {
  const { dataset, datasetMode } = useDatasetDataContext();
  const { openModal, openFlyout } = useDatasetLayoutContext();

  const files = useProjectFilesStore((s) => s.files);

  const actionUnassignFile = useDatasetAction("unassignFile");
  const uploadFileAction = useDatasetAction("uploadFile");
  const { actions: projectActions } =
    useGetFileBrowserActions<FlyoutFilesFile>(openModal);

  const selection = useMemo<FileBrowserProps<FlyoutFilesFile>["selection"]>(
    () => ({
      actions: {
        ...projectActions,
        unassign: {
          onExecute: (files) =>
            new Promise((res) => {
              const unassignableFiles = files.filter(
                (
                  file,
                ): file is FlyoutFilesFile & {
                  assignments: [
                    {
                      recordingId: FileRecordingGroup["id"];
                      recordingName: string;
                      columnId: DatasetRecordingsTableColumn["id"];
                    },
                  ];
                } =>
                  file.source === FileSource.UPLOADED &&
                  isDefined(file.assignments) &&
                  file.assignments.length === 0 &&
                  "columnId" in file.assignments[0],
              );
              openModal({
                type: "confirmAction",
                props: {
                  body: (
                    <EuiText>
                      Are you sure you wish to unassign the following{" "}
                      {unassignableFiles.length} file(s):
                      <p>
                        {unassignableFiles.map(({ id, name }) => (
                          <div key={id}>{name}</div>
                        ))}
                      </p>
                    </EuiText>
                  ),
                  onConfirm: () => {
                    unassignableFiles.forEach((file) => {
                      void actionUnassignFile.enqueue({
                        columnId: file.assignments[0]?.columnId,
                        recordingId: file.assignments[0]?.recordingId,
                        drsFileId: file.id,
                      });
                    });
                    // we treat enqueuing actions on the dataset as a success
                    res({
                      successfulFiles: unassignableFiles,
                      errors: [],
                    });
                  },
                  onClose: () => res({ successfulFiles: [], errors: [] }),
                },
              });
            }),
          suppressLoading: true,
          isDisabled: (files) =>
            !files.some(
              ({ source, assignments }) =>
                source === FileSource.UPLOADED && isDefined(assignments),
            ),
          disabledToolTip: "You must select at least one assigned file.",
        },
      },
    }),
    [actionUnassignFile, openModal, projectActions],
  );

  const fileBrowserFiles: FlyoutFilesFile[] = useMemo(
    () =>
      files.map((projectFile) => {
        if (projectFile.source === FileSource.UPLOADED) {
          const formattedAssignments: FlyoutFilesFile["assignments"] =
            // only one recording possible for uploaded files
            projectFile.recordings?.[0] !== undefined &&
            projectFile.columns?.[0] !== undefined
              ? [
                  {
                    dataset: projectFile.recordings[0].dataset,
                    columnId: projectFile.columns[0].id,
                    recording: projectFile.recordings[0],
                  },
                ]
              : [];

          return {
            ...projectFile,
            source: FileSource.UPLOADED,
            assignments: formattedAssignments,
          };
        }

        return {
          ...projectFile,
          assignments: projectFile.recordings.map(
            ({ id, number, dataset }) => ({
              dataset,
              recording: { id, number },
            }),
          ),
        };
      }),

    [files],
  );

  const { isDraggingFiles } = useDragState();

  const onDropFiles = (files: File[]) => {
    files.forEach(
      (file) =>
        void uploadFileAction.enqueue({
          blob: file,
          id: uuid(),
          name: file.name,
          datasetId: dataset.id,
          tenantId: dataset.tenantId,
          size: String(file.size),
        }),
    );
  };

  return (
    <Flyout
      onClose={onClose}
      title="File Browser"
      titleSpacerSize="s"
      {...props}
    >
      <EuiFlexItem css={styles.mainFlexContainer}>
        {datasetMode.type === "current" &&
          (isDraggingFiles || fileBrowserFiles.length === 0) && (
            <DropZoneBasic
              isClickable={fileBrowserFiles.length === 0}
              onSelectFiles={onDropFiles}
              css={styles.dropZone}
            />
          )}
        {fileBrowserFiles.length === 0 && datasetMode.type !== "current" && (
          <div>
            <EuiText>
              No files were present in this dataset at the timestamp you are
              currently viewing
            </EuiText>
          </div>
        )}
        {fileBrowserFiles.length > 0 && (
          <FileBrowser
            defaultColumnOrder={flyoutFilesDefaultColumnOrder}
            extraColumns={flyoutFilesExtraColumns}
            extraColumnProps={flyoutFilesExtraColumnProps}
            css={styles.fileBrowser}
            defaultHiddenColumns={["dataset" as const]}
            datasetFilter={[
              {
                value: "CURRENT_DATASET",
                inputDisplay: "Current Dataset",
                onFilter: (file) =>
                  file.datasets.some(({ id }) => id === dataset.id),
                default: true,
              },
              {
                value: "NO_DATASET",
                inputDisplay: "No Dataset Assigned",
                onFilter: (file) => file.datasets.length === 0,
              },
            ]}
            defaultSource="UPLOADED"
            selection={selection}
            files={fileBrowserFiles}
            fileRenderer={({ file }) => (
              <DraggableFileBadge
                drsFile={file}
                suppressContextMenu={true}
                // Disable dragging in fullscreen mode
                // There is currently some odd behavior where dnd
                // drop events on the grid are propagating through the
                // fullscreen file browser
                // https://inscopix.atlassian.net/browse/ID-2010
                disabled={file.source !== FileSource.UPLOADED}
                onClick={() => {
                  openFlyout({
                    type: "fileInfo",
                    props: { drsFile: { ...file } },
                  });
                }}
              />
            )}
          />
        )}
      </EuiFlexItem>
    </Flyout>
  );
});
